import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useReportFilter } from "../context/ReportFilterContext";
import { getValidToken } from "../../../utils/authUtils";
import axios from "axios";
import { Loading } from "../../../components/Loading";
import { formatCurrency, formatVehicleNumber } from "../../../utils/utils";
import TripDetails from "./TripDetails";
import VehicleExpenseDetails from "./VehicleExpenseDetails";
import EmiDetails from "./EmiDetails";
import { useNavigate } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function ReportByVehiclesTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 1, px: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ReportByVehicles = () => {
  const { filterState } = useReportFilter();
  const { timeFilter, customStartDate, customEndDate, selectedVehicles } =
    filterState;
  const [expandedPanel, setExpandedPanel] = React.useState<string | false>(
    false
  );
  const navigate = useNavigate();

  const handleAccordionChange =
    (vehicleNumber: string) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? vehicleNumber : false);
    };

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { data: reportByVehiclesData, isLoading } = useQuery({
    queryKey: [
      "reportByVehicles",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/expense-by-vehicle/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/expense-by-vehicle/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicles.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: true,
  });

  if (isLoading) {
    return (
      <Loading message="Loading report by vehicles..." fullScreen={true} />
    );
  }

  if (
    !reportByVehiclesData?.data ||
    reportByVehiclesData?.data?.vehicleReports.length === 0
  ) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="h6" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  const netIncome = (vehicle: any) => {
    return vehicle.totalIncome - vehicle.totalExpense;
  };

  return (
    <Paper sx={{ p: 2 }} elevation={0}>
      {reportByVehiclesData?.data?.vehicleReports.map((vehicle: any) => (
        <Accordion
          expanded={expandedPanel === vehicle.vehicleNumber}
          onChange={handleAccordionChange(vehicle.vehicleNumber)}
          sx={{
            mb: 1,
            "&:before": {
              display: "none",
            },
            boxShadow: "none",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <AccordionSummary
            sx={{
              "& .MuiAccordionSummary-content": {
                justifyContent: "space-between",
                alignItems: "center",
              },
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={1}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: "12px",
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "primary.main",
                  }}
                  onClick={() => {
                    navigate(`/vehicles/${vehicle.vehicleId}`);
                  }}
                >
                  {formatVehicleNumber(vehicle.vehicleNumber)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={10}>
                <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                  Net Income:{" "}
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: netIncome(vehicle) > 0 ? "#2e7d32" : "#d32f2f",
                      fontWeight: 500,
                    }}
                  >
                    {formatCurrency(netIncome(vehicle))}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={1}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                  }}
                >
                  {expandedPanel === vehicle.vehicleNumber
                    ? "Hide"
                    : "Show Details"}
                </Typography>
              </Grid>
              <Grid item container xs={12} sm={12}>
                {/* Income Row */}
                <Grid item container xs={12} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Bus Income:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "success.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalTripBusIncome)}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Luggage Income:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "success.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalTripLuggageIncome)}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Commission:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalTripCommission)}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Pickup Man Commission:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalTripPickupCommission)}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Total Income (Bus + Luggage - Commission):{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "success.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalIncome)}
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>

                {/* Expense Row */}
                <Grid item container xs={12}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Trip Expense:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalTripExpense)}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Vehicle Expense:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalVehicleExpense)}
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      EMI:{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalLoanPaid)} (Paid) -{" "}
                        {formatCurrency(vehicle.totalLoanUnpaid)} (Unpaid)
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ fontSize: "0.7rem" }}>
                      Total Expense (Trip + Vehicle + EMI Paid):{" "}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color: "error.main",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(vehicle.totalExpense)}
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="vehicle report tabs"
                  sx={{
                    "& .MuiTab-root": {
                      fontSize: "0.7rem",
                      minHeight: "32px",
                      textTransform: "none",
                    },
                  }}
                >
                  <Tab label="Trips" {...a11yProps(0)} />
                  <Tab label="Vehicle Expense" {...a11yProps(1)} />
                  <Tab label="Emi" {...a11yProps(2)} />
                </Tabs>
              </Box>

              <ReportByVehiclesTabPanel value={value} index={0}>
                <TripDetails data={vehicle.trips} />
              </ReportByVehiclesTabPanel>

              <ReportByVehiclesTabPanel value={value} index={1}>
                <VehicleExpenseDetails data={vehicle.vehicleExpenses} />
              </ReportByVehiclesTabPanel>

              <ReportByVehiclesTabPanel value={value} index={2}>
                <EmiDetails data={vehicle.emiData} />
              </ReportByVehiclesTabPanel>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default ReportByVehicles;
